import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useBreadcrumb, useBreadcrumbActions } from 'shared/context/BreadcrumbContext';
import { ACTION_BTN, ACTION_BTN_PRIMARY } from 'shared/styles/topBarActions';
import { useAppDialog } from 'shared/context/AppDialogContext';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import SegmentedTabs from 'shared/components/ui/SegmentedTabs';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { rowLabel, inputCls } from 'features/settings/components/SettingsRows';
import MessageEditor from 'features/alerts/components/MessageEditor';
import AlertFilters from 'features/alerts/components/AlertFilters';
import SlackChannelPicker from 'features/agents/WorkflowEditor/SlackChannelPicker';
import {
  getAlertCatalog, getAlertSchema, getAlert, createAlert, updateAlert,
  deleteAlert, setAlertEnabled, previewAlert, buildAlertTemplate,
} from 'features/alerts/api/alertsApi';
import { unitSuffix, formatValue } from 'features/alerts/format';
import { listLineages } from 'features/evals3/api/evals3Api';
import { usePermissions, PERMISSIONS } from 'shared/hooks/usePermissions';

/**
 * One page, the Settings grammar: a semibold title over a hairline, then rows
 * of a muted label and its control. Nothing is explained that the control
 * itself does not already say.
 */

const INPUT = inputCls.replace('w-full ', '');

/** The Settings grammar, tighter: a title over a hairline, rows of muted label and control. */
function Section({ title, children }) {
  return (
    <section className="mb-4">
      <h2 className="border-b border-[color:var(--border-default)] pb-1.5 text-[13px] font-semibold text-[color:var(--text-primary)]">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8">{children}</div>
    </section>
  );
}
function Row({ label, children, align = 'center', wide = false }) {
  return (
    <div className={`flex flex-col gap-1 border-b border-[color:var(--border-subtle)] py-2 sm:flex-row sm:gap-3 ${align === 'start' ? 'sm:items-start' : 'sm:items-center'} ${wide ? 'md:col-span-2' : ''}`}>
      <div className={`${rowLabel} sm:w-24`}>{label}</div>
      <div className="flex min-w-0 flex-1 items-center gap-3">{children}</div>
    </div>
  );
}

const VARS = ['name', 'measure', 'value', 'operator', 'threshold', 'window', 'count', 'units', 'link'];
const SCOPE_OPTIONS = [
  { value: 'run', label: 'Run' },
  { value: 'span', label: 'Span' },
  { value: 'eval', label: 'Eval' },
];
/** An eval alert can also say which eval, and where its failures ended. */
const EVAL_VARS = ['eval', 'endings'];
/**
 * `{{units}}` rather than a noun baked in here: the message is written once,
 * when the form loads, and switching Run → Span afterwards used to leave a
 * span alert announcing "5 runs" in Slack. An eval alert's default also
 * names the eval and where its failures ended.
 */
const RUNSPAN_MESSAGE = '*{{name}}*\n{{measure}}: {{value}} ({{operator}} {{threshold}}) over the last {{window}} · {{count}} {{units}}\n{{link}}';
const EVAL_MESSAGE = '*{{name}}* · {{eval}}\n{{measure}}: {{value}} ({{operator}} {{threshold}}) over the last {{window}} · {{count}} {{units}}\nMost failures: {{endings}}\n{{link}}';
const OLD_MESSAGE = '*{{name}}*\n{{measure}}: {{value}} ({{operator}} {{threshold}}) over the last {{window}} · {{count}} {{scope}}s\n{{link}}';
const defaultMessage = (scope = 'run') => (scope === 'eval' ? EVAL_MESSAGE : RUNSPAN_MESSAGE);
const isDefaultMessage = (m) => [RUNSPAN_MESSAGE, EVAL_MESSAGE, OLD_MESSAGE].includes(m);
/** What one row of each kind is called, where the form says so. */
const ROW_WORD = { run: 'runs', span: 'spans', eval: 'decisions' };

const UNITS = [{ value: '60', label: 'minutes' }, { value: '3600', label: 'hours' }, { value: '86400', label: 'days' }];
/** 900 → { n: 15, unit: 60 }; picks the largest unit that divides evenly. */
function splitWindow(seconds) {
  const s = Number(seconds) || 900;
  if (s % 86400 === 0) return { n: s / 86400, unit: '86400' };
  if (s % 3600 === 0) return { n: s / 3600, unit: '3600' };
  return { n: Math.max(1, Math.round(s / 60)), unit: '60' };
}

const EMPTY = {
  name: '', scope: 'run', eval_lineage_id: '', measure: 'runs', aggregation: 'count',
  filter: { groups: [] }, operator: 'above', threshold: '', window_n: 15, window_unit: '60',
  min_sample: 1, message: defaultMessage(), slack_channel_id: '', webhook_url: '',
};

export default function AlertEditPage({ alertId }) {
  const canWrite = usePermissions().can(PERMISSIONS.APP_USE);
  const isNew = !alertId || alertId === 'new';
  const { team } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [evals, setEvals] = useState(null);
  const { confirm } = useAppDialog();

  const [catalog, setCatalog] = useState(null);
  const [schema, setSchema] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null); // the stored alert when editing
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sendTab, setSendTab] = useState('slack');
  const teamId = useMemo(() => { try { return JSON.parse(localStorage.getItem('selectedTeamData'))?.id; } catch { return null; } }, []);


  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cat, existing] = await Promise.all([getAlertCatalog(), isNew ? null : getAlert(alertId)]);
        if (cancelled) return;
        setCatalog(cat);
        // Arriving from an eval's page ("Create alert"): the eval is chosen already.
        if (!existing) {
          const q = new URLSearchParams(location.search);
          if (q.get('scope') === 'eval') {
            setForm((f) => ({ ...f, scope: 'eval', eval_lineage_id: q.get('eval') || '', measure: 'failures', aggregation: 'count', message: defaultMessage('eval') }));
          }
          // From a template (the Alerts list, or "Create alert" on a Failures
          // row): filled in, not saved.
          if (q.get('template')) {
            const narrow = {};
            for (const k of ['agent', 'tool', 'error_type']) if (q.get(k)) narrow[k] = q.get(k);
            const t = await buildAlertTemplate(q.get('template'), narrow);
            if (cancelled) return;
            const w = splitWindow(t.window_seconds);
            setForm((f) => ({
              ...f,
              name: t.name, scope: t.scope, measure: t.measure, aggregation: t.aggregation,
              filter: t.filter || { groups: [] }, operator: t.operator, threshold: String(t.threshold ?? ''),
              window_n: w.n, window_unit: w.unit, min_sample: t.min_sample ?? 1, message: defaultMessage(t.scope),
            }));
          }
        }
        if (existing) {
          setSaved(existing);
          const w = splitWindow(existing.window_seconds);
          setForm({
            name: existing.name, scope: existing.scope, eval_lineage_id: existing.eval_lineage_id || '', measure: existing.measure, aggregation: existing.aggregation,
            filter: existing.filter || { groups: [] }, operator: existing.operator, threshold: String(existing.threshold ?? ''),
            window_n: w.n, window_unit: w.unit, min_sample: existing.min_sample,
            message: existing.message || defaultMessage(existing.scope),
            slack_channel_id: existing.slack_channel_id || '', webhook_url: existing.webhook_url || '',
          });
          setSendTab(existing.webhook_url && !existing.slack_channel_id ? 'webhook' : 'slack');
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [alertId, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  // The evals an eval alert can watch: the team's own, loaded once the kind is picked.
  useEffect(() => {
    if (form.scope !== 'eval' || evals) return undefined;
    let cancelled = false;
    Promise.resolve(teamId ? listLineages(teamId) : [])
      .then((ls) => { if (!cancelled) setEvals(ls || []); })
      .catch(() => { if (!cancelled) setEvals([]); });
    return () => { cancelled = true; };
  }, [form.scope, evals, teamId]);

  // Filter fields follow the scope.
  useEffect(() => {
    let cancelled = false;
    setSchema(null);
    getAlertSchema(form.scope).then((s) => { if (!cancelled) setSchema(s); }).catch(() => {});
    return () => { cancelled = true; };
  }, [form.scope]);

  const measures = catalog?.measures?.[form.scope] || [];
  const measure = measures.find((m) => m.key === form.measure) || measures[0];
  const unit = measure?.unit || 'count';

  // Keep measure and aggregation legal when the scope or measure changes.
  useEffect(() => {
    if (!catalog) return;
    const list = catalog.measures[form.scope] || [];
    const m = list.find((x) => x.key === form.measure) || list[0];
    if (!m) return;
    const patch = {};
    if (m.key !== form.measure) patch.measure = m.key;
    if (!m.aggregations.includes(form.aggregation)) patch.aggregation = m.aggregations[0];
    if (Object.keys(patch).length) set(patch);
  }, [catalog, form.scope, form.measure, form.aggregation]);

  const windowSeconds = Math.round((Number(form.window_n) || 0) * Number(form.window_unit));
  const problems = useMemo(() => {
    const p = [];
    if (!form.name.trim()) p.push('name');
    if (form.scope === 'eval' && !form.eval_lineage_id) p.push('eval');
    if (!(windowSeconds >= 60 && windowSeconds <= 30 * 86400)) p.push('window');
    if (form.threshold === '' || !Number.isFinite(Number(form.threshold))) p.push('threshold');
    if (!form.slack_channel_id && !form.webhook_url) p.push('destination');
    if (form.webhook_url && !/^https?:\/\//i.test(form.webhook_url)) p.push('webhook');
    return p;
  }, [form, windowSeconds]);

  // What the alert reads right now, for the condition as it stands: the value,
  // how many rows made it, and whether it would trigger. Nothing is saved.
  const [now, setNow] = useState(null);
  const previewKey = JSON.stringify([form.scope, form.eval_lineage_id, form.measure, form.aggregation, form.filter, form.operator, form.threshold, windowSeconds, form.min_sample]);
  useEffect(() => {
    if (!catalog || !(windowSeconds >= 60) || (form.scope === 'eval' && !form.eval_lineage_id)) { setNow(null); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      previewAlert({
        name: 'preview', scope: form.scope, eval_lineage_id: form.scope === 'eval' ? form.eval_lineage_id : null,
        measure: form.measure, aggregation: form.aggregation, filter: form.filter, operator: form.operator,
        threshold: Number.isFinite(Number(form.threshold)) && form.threshold !== '' ? Number(form.threshold) : 0,
        window_seconds: windowSeconds, min_sample: Math.max(1, Number(form.min_sample) || 1), slack_channel_id: 'preview',
      }).then((r) => { if (!cancelled) setNow(r); }).catch(() => { if (!cancelled) setNow(null); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [previewKey, catalog]); // eslint-disable-line react-hooks/exhaustive-deps

  const body = () => ({
    ...form,
    eval_lineage_id: form.scope === 'eval' ? form.eval_lineage_id : null,
    threshold: Number(form.threshold),
    window_seconds: windowSeconds,
    min_sample: Math.max(1, Number(form.min_sample) || 1),
    slack_channel_id: form.slack_channel_id || null,
    webhook_url: form.webhook_url || null,
    message: form.message || null,
  });

  const save = async () => {
    if (problems.length) return;
    setSaving(true); setError('');
    try {
      const saved = isNew ? await createAlert(body()) : await updateAlert(alertId, body());
      navigate(`/${team}/alerts/${saved.id}`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setSaving(false);
    }
  };

  const remove = async () => {
    const ok = await confirm(`"${saved?.name || form.name}" will be removed.`, { title: 'Delete alert', confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    await deleteAlert(alertId);
    navigate(`/${team}/alerts`);
  };

  const toggle = async () => {
    const next = await setAlertEnabled(alertId, !saved.enabled);
    setSaved(next);
  };

  // Path and controls go in the app's own top bar, so this page reads exactly
  // like every other one. Declared before the loading guard because hooks are.
  useBreadcrumb(useMemo(() => [
    { label: 'Alerts', to: `/${team}/alerts` },
    { label: isNew ? 'New alert' : (saved?.name || '…') },
  ], [team, isNew, saved]));

  useBreadcrumbActions(
    !canWrite ? null : <div className="flex items-center gap-2">
      {!isNew && saved && (
        <button type="button" onClick={toggle} className={ACTION_BTN}>
          {saved.enabled ? 'Pause' : 'Resume'}
        </button>
      )}
      {!isNew && (
        <button type="button" onClick={remove} className={ACTION_BTN}>Delete</button>
      )}
      <button type="button" onClick={save} disabled={saving || problems.length > 0} className={ACTION_BTN_PRIMARY}>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {isNew ? 'Create' : 'Save'}
      </button>
    </div>,
  );

  if (loading || !catalog) {
    return <PageLoader label="Loading alert" />;
  }

  const aggOptions = (measure?.aggregations || []).map((a) => ({ value: a, label: catalog.aggregations[a] || a }));
  const opOptions = Object.entries(catalog.operators).map(([k, o]) => ({ value: k, label: o.label }));

  const rowWord = ROW_WORD[form.scope] || `${form.scope}s`;

  return (
    <div className="flex h-full min-h-0 flex-col bg-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[880px] px-5 py-4">
          <Section title="Alert">
            <Row label="Name">
              <input className={`${INPUT} w-full`} aria-label="Name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Checkout p95 latency" />
            </Row>
            <Row label="Scope">
              <SegmentedTabs
                value={form.scope} ariaLabel="Scope"
                // The message follows the kind while it is still a default one.
                onChange={(v) => set({ scope: v, filter: { groups: [] }, ...(isDefaultMessage(form.message) ? { message: defaultMessage(v) } : {}) })}
                options={SCOPE_OPTIONS}
              />
            </Row>
            {form.scope === 'eval' && (
              <Row label="Eval" wide>
                <div className="w-full max-w-sm">
                  <BrandDropdown value={form.eval_lineage_id} ariaLabel="Eval" placeholder={evals ? (evals.length ? 'Pick an eval' : 'No evals yet') : 'Loading evals…'}
                    onChange={(v) => set({ eval_lineage_id: v, filter: { groups: [] } })}
                    options={(evals || []).map((l) => ({ value: l.id, label: l.name }))} />
                </div>
              </Row>
            )}
          </Section>

          <Section title="Watch">
            <Row label="Measure">
              <div className="w-full">
                <BrandDropdown ariaLabel="Measure" value={form.measure} onChange={(v) => set({ measure: v })} options={measures.map((m) => ({ value: m.key, label: m.label }))} />
              </div>
            </Row>
            <Row label="Aggregation">
              <div className="w-full">
                <BrandDropdown ariaLabel="Aggregation" value={form.aggregation} onChange={(v) => set({ aggregation: v })} options={aggOptions} disabled={aggOptions.length < 2} />
              </div>
            </Row>
            <Row label="Filters" align="start" wide>
              <div className="min-w-0 flex-1">
                <AlertFilters value={form.filter} onChange={(v) => set({ filter: v })} schema={schema} scope={form.scope}
                  evalLineageId={form.scope === 'eval' ? form.eval_lineage_id || null : null} />
              </div>
            </Row>
          </Section>

          <Section title="Condition">
            <Row label="When" wide>
              <div className="flex flex-wrap items-center gap-2 text-[13px] text-[color:var(--text-muted)]">
                <span className="w-[124px]"><BrandDropdown value={form.operator} onChange={(v) => set({ operator: v })} options={opOptions} ariaLabel="Operator" /></span>
                <span className="relative inline-flex items-center">
                  <input
                    aria-label="Threshold" className={`${INPUT} w-[112px] pr-9 tabular-nums`} type="number" inputMode="decimal" step="any"
                    value={form.threshold} onChange={(e) => set({ threshold: e.target.value })} placeholder="0"
                  />
                  {unitSuffix(unit) && <span className="pointer-events-none absolute right-2.5 text-[11px] text-[color:var(--text-muted)]">{unitSuffix(unit)}</span>}
                </span>
                <span className="whitespace-nowrap">over the last</span>
                <input
                  aria-label="Window" className={`${INPUT} w-[64px] tabular-nums`} type="number" min={1} inputMode="numeric"
                  value={form.window_n} onChange={(e) => set({ window_n: e.target.value })}
                />
                <span className="w-[104px]"><BrandDropdown value={form.window_unit} onChange={(v) => set({ window_unit: v })} options={UNITS} ariaLabel="Window unit" /></span>
              </div>
            </Row>
            {/* A rate needs a few rows before it means anything: one failing
                run is a 100% error rate. Said out loud rather than left as an
                invisible floor that makes an alert look broken. */}
            {(() => {
              const least = form.aggregation === 'rate' ? catalog?.rate_min_sample : measure?.min_sample;
              return least > 1 ? <Row label="Minimum"><span className="text-[13px] font-normal text-[color:var(--text-primary)]">{`${least} ${rowWord} in the window`}</span></Row> : null;
            })()}
            {now && (
              <Row label="Right now">
                <span className="text-[13px] text-[color:var(--text-secondary)]" data-testid="alert-now">
                  <span className="font-medium tabular-nums text-[color:var(--text-primary)]">{formatValue(now.value, now.unit || unit)}</span>
                  {/* A count counts the very rows it was measured over, so "4
                      from 4 decisions" is the same number twice and reads like
                      everything failed. The sample only tells you something
                      where the value is a share or an average of it. */}
                  {form.aggregation !== 'count' && (
                    <span className="text-[color:var(--text-muted)]"> from {now.sample_size} {rowWord}</span>
                  )}
                  {' · '}
                  {now.state === 'no_data'
                    ? <span className="text-[color:var(--text-muted)]">not enough to decide</span>
                    : form.threshold === ''
                      ? <span className="text-[color:var(--text-muted)]">set a threshold</span>
                      : now.state === 'alerting'
                        ? <span className="font-medium text-danger">would trigger</span>
                        : <span className="text-success">would not trigger</span>}
                </span>
              </Row>
            )}
          </Section>

          <Section title="Notify">
            <Row label="Message" align="start" wide>
              <MessageEditor value={form.message} onChange={(m) => set({ message: m })} variables={[...VARS, ...(form.scope === 'eval' ? EVAL_VARS : [])]} />
            </Row>
            <Row label="Send to" align="start" wide>
              <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
                <SegmentedTabs
                  value={sendTab} onChange={setSendTab} ariaLabel="Send to"
                  options={[{ value: 'slack', label: 'Slack' }, { value: 'webhook', label: 'Webhook' }]}
                />
                <div className="w-full max-w-sm">
                  {sendTab === 'slack' ? (
                    <SlackChannelPicker
                      field={{ key: 'channel', label: 'Channel' }} nodeKind="slack_send_message" showHelp={false}
                      value={form.slack_channel_id} onChange={(v) => set({ slack_channel_id: v })}
                    />
                  ) : (
                    <input className={`${INPUT} w-full`} aria-label="Webhook URL" value={form.webhook_url} onChange={(e) => set({ webhook_url: e.target.value })} placeholder="https://" />
                  )}
                </div>
              </div>
            </Row>
          </Section>

          {error && <p className="text-[12px] text-danger">{error}</p>}
        </div>
      </div>
    </div>
  );
}
